/**
 * Env-presence panel. Hits /api/marketing/diag on mount and renders
 * a compact ✓/✗ grid for each env var + binding the marketing
 * pipeline needs. Self-contained — drop it anywhere on a page.
 *
 * Auto-opens (and bolds the row) when something critical is missing
 * so admins can't miss a misconfigured Preview scope. Stays collapsed
 * once everything's present.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../lib/api';

interface EnvCheckResponse {
  supabase_url: boolean;
  supabase_anon_key: boolean;
  openai_api_key: boolean;
  elevenlabs_api_key: boolean;
  pexels_api_key: boolean;
  r2_binding: boolean;
  r2_public_base_url: boolean;
}

const KEYS: { name: keyof EnvCheckResponse; label: string; critical: boolean }[] = [
  { name: 'elevenlabs_api_key', label: 'ELEVENLABS_API_KEY', critical: true },
  { name: 'r2_binding', label: 'R2 binding', critical: true },
  { name: 'r2_public_base_url', label: 'R2_PUBLIC_BASE_URL', critical: true },
  { name: 'openai_api_key', label: 'OPENAI_API_KEY', critical: true },
  { name: 'supabase_url', label: 'SUPABASE_URL', critical: true },
  { name: 'supabase_anon_key', label: 'SUPABASE_ANON_KEY', critical: true },
  // Optional — Seed-from-stock fallback. The Seed Suggestions banner
  // works with or without this key (without → search links only).
  { name: 'pexels_api_key', label: 'PEXELS_API_KEY (optional)', critical: false },
];

export function EnvDiagnostic() {
  const [envCheck, setEnvCheck] = useState<EnvCheckResponse | null>(null);
  const [checking, setChecking] = useState(false);

  const run = useCallback(async () => {
    setChecking(true);
    try {
      const res = await apiJson<EnvCheckResponse>('/api/marketing/diag');
      setEnvCheck(res);
    } catch (e) {
      console.warn('[env-diag] failed:', e);
      // Fall back to zeros so the panel still renders with all-missing.
      setEnvCheck({
        supabase_url: false,
        supabase_anon_key: false,
        openai_api_key: false,
        elevenlabs_api_key: false,
        pexels_api_key: false,
        r2_binding: false,
        r2_public_base_url: false,
      });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  if (!envCheck) return null;

  const missingCritical = KEYS.filter((k) => k.critical && !envCheck[k.name]);

  return (
    <details
      open={missingCritical.length > 0}
      style={{
        marginTop: 16,
        background:
          missingCritical.length > 0 ? 'rgba(232, 67, 45, 0.08)' : 'var(--color-surface-alt)',
        border:
          missingCritical.length > 0
            ? '1px solid rgba(232, 67, 45, 0.4)'
            : '1px solid transparent',
        borderRadius: 10,
        padding: '10px 14px',
      }}
    >
      <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
        {missingCritical.length > 0
          ? `⚠ Pages env check — ${missingCritical.length} missing`
          : '✓ Pages env check — all present'}
      </summary>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, fontSize: 13 }}>
        {KEYS.map((k) => (
          <span
            key={String(k.name)}
            style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
          >
            <span style={{ color: envCheck[k.name] ? 'limegreen' : 'crimson', fontWeight: 700 }}>
              {envCheck[k.name] ? '✓' : '✗'}
            </span>
            <code>{k.label}</code>
          </span>
        ))}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={checking}
          onClick={run}
          style={{ marginLeft: 'auto' }}
        >
          {checking ? 'Checking…' : 'Re-check'}
        </button>
      </div>
      {missingCritical.length > 0 && (
        <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
          Add the missing variables in Cloudflare Pages → tripcode-marketing → Settings →
          Environment variables, scoped to <strong>Preview</strong> for this build (and{' '}
          <strong>Production</strong> when you ship). Trigger a fresh deploy (any push to the PR
          will do it) to pick up new values.
        </p>
      )}
    </details>
  );
}
